class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // Re-enabled 4 Jun 2026: cart icon opens the slide cart instead of going to /cart.
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      // In-app-browser fix (2 Jul 2026): if open() throws (e.g. a helper from global.js is
      // missing because an old FB/IG WebView failed to parse it), don't leave the buyer with a
      // dead cart icon — fall back to normal navigation to the cart page.
      try {
        this.open(cartLink);
      } catch (e) {
        window.location.href = cartLink.getAttribute('href') || (window.routes && window.routes.cart_url) || '/cart';
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
          const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.updateCartUpsellToggleState();
      cartDrawerItems.updateCartUpsellVisibility();
    }

    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer') || this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        // Guard: on some templates (e.g. gallery wall pages) the focus container may not be in the DOM
        // at transitionend. Skip the focus trap rather than crashing the drawer's open routine.
        // typeof guard added 2 Jul 2026: trapFocus lives in global.js — if that file failed to
        // parse in an old in-app WebView, calling it would throw a ReferenceError here.
        if (containerToTrapFocusOn && typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // In-app-browser fix (2 Jul 2026): unlock body scroll BEFORE the focus-trap call. If
    // removeTrapFocus is missing (global.js parse failure in an old FB/IG WebView) the old order
    // threw before removing overflow-hidden and wedged the whole page unscrollable.
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    // typeof guard (2 Jul 2026): onKeyUpEscape lives in global.js — see trapFocus note above.
    if (typeof onKeyUpEscape === 'function') cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // In-app-browser fix (2 Jul 2026): renderContents is only ever called AFTER a successful
    // /cart/add, so the item is already in the cart. If anything below fails (Shopify omitted
    // `sections` from the response — seen when section rendering is throttled — or a target
    // element is missing, or the HTML fails to parse) the buyer used to get a TypeError: no
    // drawer, no feedback, and on the PDP the catch-handler re-submitted the form natively and
    // double-added the item. Instead, fall back to the full cart page: always a path to checkout.
    try {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
      // Fix (5 Jun): when the cart loaded empty, the <cart-drawer> element keeps its initial
      // `is-empty` class. The section render below refreshes #CartDrawer's contents but not
      // this element's own class, and `cart-drawer.is-empty` CSS hides the header and collapses
      // the freshly-rendered items to 0 height. Clear it now that the cart has items, so they show.
      this.classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    } catch (e) {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      // Re-enabled 4 Jun 2026: auto-open the slide cart on add-to-cart (pop-up removed).
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
